import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

type OtpItem = {
  code: string;
  purpose: string;
  createdAt: string;
  email?: string;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://web-app-win-dev.azurewebsites.net";

const OTP_ENDPOINT = `${API_BASE.replace(/\/$/, "")}/testing/otp`;

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

const OTPPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<OtpItem[]>([]);

  const LIMIT = 20; // default number of records

  const fetchOtp = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const addr = email.trim();
      const query = addr ? `?email=${encodeURIComponent(addr)}` : "";
      const url = `${OTP_ENDPOINT}${query}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { accept: "application/json" },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data: unknown = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format");
      }
      const normalized: OtpItem[] = (data as Array<Record<string, unknown>>)
        .filter(
          (x) =>
            x &&
            typeof x === "object" &&
            "code" in x &&
            "purpose" in x &&
            "createdAt" in x
        )
        .map((x) => ({
          code: String(x.code),
          purpose: String(x.purpose),
          createdAt: String(x.createdAt),
          // Accept both `email` and `Email` from backend
          email:
            "email" in x && x.email
              ? String(x.email)
              : "Email" in x && x.Email
              ? String(x.Email)
              : undefined,
        }))
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, LIMIT);
      setItems(normalized);
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Failed to load";
      setError(errorMessage);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const hasItems = items.length > 0;

  const header = useMemo(
    () => (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          label="Email"
          value={email}
          type="email"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") fetchOtp();
          }}
          sx={{
            width: { xs: "100%", sm: 520 },
            "& .MuiInputBase-input": {
              fontSize: 18,
              fontWeight: 500,
              py: 1.25,
            },
          }}
        />
        <Button
          onClick={fetchOtp}
          variant="contained"
          size="medium"
          disabled={loading}
          sx={{
            height: 44,
            fontSize: 16,
            fontWeight: 700,
            px: 3,
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Loading…" : "Get OTP"}
        </Button>
      </Stack>
    ),
    [email, fetchOtp, loading]
  );

  // Load default last 20 on mount (no email filter)
  React.useEffect(() => {
    fetchOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        OTP Codes (Testing)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Public page. Loads last 20 codes by default. Enter email to filter.
      </Typography>

      <Box sx={{ my: 3 }}>{header}</Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper variant="outlined">
        {!hasItems ? (
          <Box sx={{ p: 3, color: "text.secondary" }}>
            No results yet. Run a query.
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item, idx) => (
              <ListItem key={`${item.code}-${idx}`} divider>
                <ListItemText
                  primaryTypographyProps={{
                    variant: "h6",
                    sx: { fontWeight: 700 },
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: { fontSize: 12.5 },
                  }}
                  primary={`${item.purpose}: ${item.code}${
                    item.email ? ` — ${item.email}` : ""
                  }`}
                  secondary={formatDate(item.createdAt)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default OTPPage;
